import { trackAuthAnalyticsEvent } from "./authTelemetry";
import {
  addAppBreadcrumb,
  reportAppEvent,
  reportUserVisibleFailure,
} from "./reportAppError";
import { isFirestorePermissionDeniedError } from "./firestoreAuthReady";
import { isFirestoreInternalAssertionError } from "./firestoreInternalAssertion";

const NETWORK_ERROR_CODES = new Set([
  "auth/network-request-failed",
  "cancelled",
  "deadline-exceeded",
  "network-request-failed",
  "resource-exhausted",
  "unavailable",
]);

const normalizeErrorCode = (error) => {
  const code = String(error?.code || "").trim();
  return code || "unknown";
};

export function classifyAuthBootstrapError(error) {
  const message = String(error?.message || "");
  const errorCode = normalizeErrorCode(error);

  if (isFirestorePermissionDeniedError(error)) return "permission_denied";
  if (isFirestoreInternalAssertionError(error)) return "firestore_internal_assertion";
  if (/Account setup is still finishing/i.test(message)) return "incomplete_account";
  if (/User document not found/i.test(message)) return "missing_user_document";
  if (
    NETWORK_ERROR_CODES.has(errorCode)
    || /network|offline|failed to fetch|connection/i.test(message)
  ) {
    return "network";
  }
  return "unknown";
}

export function getAuthBootstrapDiagnostics({
  error,
  retryCount = 0,
  automaticAttemptCount = 0,
  startedAt = null,
  now = Date.now(),
  navigatorObject = typeof navigator === "undefined" ? null : navigator,
} = {}) {
  const connection = navigatorObject?.connection
    || navigatorObject?.mozConnection
    || navigatorObject?.webkitConnection
    || null;
  const elapsedMs = Number.isFinite(startedAt)
    ? Math.max(0, now - startedAt)
    : null;

  return {
    failureCategory: classifyAuthBootstrapError(error),
    errorCode: normalizeErrorCode(error),
    retryCount,
    isRetry: retryCount > 0,
    automaticAttemptCount,
    timeToFailureMs: elapsedMs,
    networkOnline: typeof navigatorObject?.onLine === "boolean"
      ? navigatorObject.onLine
      : null,
    connectionType: connection?.type || null,
    connectionEffectiveType: connection?.effectiveType || null,
    connectionSaveData: typeof connection?.saveData === "boolean"
      ? connection.saveData
      : null,
  };
}

const analyticsFields = (diagnostics) => ({
  failure_category: diagnostics.failureCategory,
  error_code: diagnostics.errorCode,
  retry_count: diagnostics.retryCount,
  network_online: diagnostics.networkOnline,
  connection_effective_type: diagnostics.connectionEffectiveType,
  time_to_failure_ms: diagnostics.timeToFailureMs,
});

export function reportAuthBootstrapBlocked({
  error,
  uid,
  route,
  retryCount,
  automaticAttemptCount,
  startedAt,
  navigatorObject,
} = {}) {
  const diagnostics = getAuthBootstrapDiagnostics({
    error,
    retryCount,
    automaticAttemptCount,
    startedAt,
    navigatorObject,
  });

  trackAuthAnalyticsEvent("auth_bootstrap_blocked", analyticsFields(diagnostics));
  reportUserVisibleFailure(error, {
    feature: "authentication",
    action: "auth_bootstrap_blocked",
    route,
    uid,
    ...diagnostics,
  });

  return diagnostics;
}

export function reportAuthBootstrapRetry({
  uid,
  route,
  retryCount,
  failureCategory = "unknown",
} = {}) {
  const context = {
    feature: "authentication",
    action: "auth_bootstrap_retry",
    route,
    uid,
    retryCount,
    isRetry: true,
    failureCategory,
  };

  trackAuthAnalyticsEvent("auth_bootstrap_retry", {
    retry_count: retryCount,
    failure_category: failureCategory,
  });
  addAppBreadcrumb({
    feature: "authentication",
    action: "auth_bootstrap_retry",
    data: context,
  });
  reportAppEvent("Auth bootstrap retry requested", context);
}

export function reportAuthBootstrapRecovered({
  uid,
  route,
  retryCount,
  startedAt,
  failureCategory = "unknown",
  now = Date.now(),
} = {}) {
  const timeToRecoveryMs = Number.isFinite(startedAt)
    ? Math.max(0, now - startedAt)
    : null;
  const context = {
    feature: "authentication",
    action: "auth_bootstrap_recovered",
    route,
    uid,
    retryCount,
    isRetry: retryCount > 0,
    failureCategory,
    timeToRecoveryMs,
  };

  trackAuthAnalyticsEvent("auth_bootstrap_recovered", {
    retry_count: retryCount,
    failure_category: failureCategory,
    time_to_recovery_ms: timeToRecoveryMs,
  });
  addAppBreadcrumb({
    feature: "authentication",
    action: "auth_bootstrap_recovered",
    data: context,
  });
  reportAppEvent("Auth bootstrap recovered", context);
}

export function reportAuthBootstrapPersistenceReset({
  error,
  uid,
  route,
  retryCount,
  automaticAttemptCount,
  startedAt,
  alreadyReset = false,
  navigatorObject,
} = {}) {
  const diagnostics = getAuthBootstrapDiagnostics({
    error,
    retryCount,
    automaticAttemptCount,
    startedAt,
    navigatorObject,
  });
  const context = {
    feature: "authentication",
    action: "auth_bootstrap_persistence_reset",
    route,
    uid,
    alreadyReset,
    ...diagnostics,
  };

  trackAuthAnalyticsEvent("auth_bootstrap_persistence_reset", {
    ...analyticsFields(diagnostics),
    already_reset: alreadyReset,
  });
  addAppBreadcrumb({
    feature: "authentication",
    action: "auth_bootstrap_persistence_reset",
    data: context,
  });
  reportAppEvent("Auth bootstrap persistence reset", context);

  return diagnostics;
}
